<template>
  <div class="text-base w-full">
    <dl class="p-4 md:p-6">
      <o-description-details
        v-if="!reservation.isGroup"
        :term="$t('general.customer')"
        class="pb-0"
      >
        <customer-select v-model="_reservation.customer" preview create default-status-class="bg-white dark:bg-gray-800/50 h-10" />
      </o-description-details>

      <o-description-details
        v-if="isRecurring && reservation.recurrence"
        :term="$t('reservation.recurrence')"
      >
        <div class="h-15 relative p-1 flex items-center justify-between rounded-md border dark:border-gray-800">
          <div class="leading-none inline-block m-1">
            <p class="text-sm opacity-80">
              {{ reservation.recurrence.label }}
            </p>
            <o-chip
              flat
              variant="info"
              :icon="null"
              size="sm"
              icon-after
              placement="bottom-end"
              class="mr-1 mt-1"
            >
              {{ $t('components.reservation.edit.info.recurrenceOf', { number: reservation.recurrence.recurrenceNumber, count: reservation.recurrence.recurrenceCount }) }}
            </o-chip>
          </div>

          <o-tooltip :text="$t('reservation.editRecurrence')">
            <o-button
              flat
              size="sm"
              icon="reservationRecurrence"
              class="inline-block"
              @click="handleRecurring"
            />
          </o-tooltip>
        </div>
      </o-description-details>

      <o-description-details
        v-if="isCourse"
        term="Course"
      >
        <div class="relative  rounded-md border dark:border-gray-800 overflow-hidden">
          <OPanelLink
            v-for="courseRunReservation in reservation.courseRunReservations"
            :key="courseRunReservation.idCourseRunReservation"
            :path="`/course/view?idCourseRun=${courseRunReservation.courseRun.idCourseRun}&idCourse=${courseRunReservation.courseRun.course.idCourse}`"
            class="leading-none p-2 flex items-center justify-between border-b last:border-none hover:bg-primary-50 dark:hover:bg-gray-800/50 transition-all"
          >
            <div>
              <p class="font-medium text-sm mb-1">
                {{ courseRunReservation.courseRun.course.name }}

                <span class="text-sm opacity-80 mb-1">
                  - {{ courseRunReservation.courseRun?.information }} <span class="font-normal opacity-70">(#{{ courseRunReservation.courseRun.idCourseRun }})</span>
                </span>

                <o-chip v-if="courseRunReservation.courseRun.status === CourseRunStatus.cancelled" size="sm" variant="danger" flat class="ml-1">
                  {{ $t('general.cancelled') }}
                </o-chip>
              </p>

              <p class="text-sm opacity-80">
                From {{ $date.convert(courseRunReservation.courseRun?.dates[0], undefined, $date.presets.long) }}
              </p>
            </div>
          </OPanelLink>
        </div>
      </o-description-details>

      <o-description-details
        v-if="!isGroup"
        :term="$t('general.price')"
      >
        <price :reservation="reservation" @update:loading="loading" @update:reservation="update" />
      </o-description-details>

      <o-description-details
        v-if="!isGroup && (reservation.order || !reservation.isPaid)"
        :term="$t('general.payment')"
      >
        <order :reservation="reservation" @update:loading="loading" @update:reservation="update" />
      </o-description-details>

      <o-description-details
        v-if="!isGroup"
        :term="$t('general.status')"
      >
        <status :reservation="reservation" editable hide-customer-alerts @update:loading="loading" @update:reservation="update" />
      </o-description-details>

      <template v-if="integrationsWithLabels">
        <o-description-details
          v-for="integration in integrationsWithLabels"
          :key="integration.integration.guid"
          :term="integration.integration.name"
        >
          <div v-for="label in integration.labels" :key="label.name">
            <span class="font-semibold">
              {{ $t('general.colon', { label: label.name }) }}
            </span>
            <span v-if="label.isText">
              {{ label.value }}
            </span>
            <a v-if="label.isUrl" :href="label.value" target="_blank">
              {{ label.value }}
            </a>
          </div>
        </o-description-details>
      </template>

      <o-description-details
        v-if="reservation.isGroup"
        :term="$t('components.reservation.edit.info.maxCustomers')"
      >
        <div>
          <number-field v-model="_reservation.customerMax" base-class="h-9 flex items-center" />
          <div class="mt-3">
            <div class="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-3 relative">
              <div
                class="rounded-full h-3 absolute"
                :class="[
                  { 'bg-green-500': attendeeCount / reservation.customerMax * 100 < 50 },
                  { 'bg-yellow-500': attendeeCount / reservation.customerMax * 100 >= 50 && attendeeCount / reservation.customerMax * 100 < 80 },
                  { 'bg-red-500': attendeeCount / reservation.customerMax * 100 >= 80 }
                ]"
                :style="{ width: attendeeCount / reservation.customerMax * 100 + '%' }"
              />
            </div>
            <div class="text-sm leading-5 mt-2">
              <div class="flex items-center justify-between">
                <span class="opacity-70">
                  {{ $t('components.reservation.edit.info.attendees', { count: attendeeCount }) }}
                </span>
                <o-chip v-if="attendeeCount === reservation.customerMax" variant="danger" size="sm">
                  {{ $t('general.full') }}
                </o-chip>
                <span v-else-if="reservation.customerMax > 0" class="opacity-70">
                  {{ $t('components.reservation.edit.info.spotsLeft', { count: reservation.customerMax - attendeeCount }) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </o-description-details>

      <o-description-details
        v-if="reservation.isGroup"
        :term="$t('general.allowOnlineBooking')"
      >
        <select-field
          v-model="_reservation.allowInternet"
          :options="[
            { label: $t('general.yes'), value: true },
            { label: $t('general.no'), value: false }
          ]"
        />
      </o-description-details>

      <o-description-details
        v-if="isGroup"
        :term="$t('general.status')"
      >
        <o-chip v-if="isStatusCancelled" class="mr-1 mb-1" variant="danger">
          {{ $t('general.cancelled') }}

          <div v-if="_reservation.cancellationType || _reservation.cancellationNote" class="ml-1">
            <p v-if="_reservation.cancellationType">
              {{ _reservation.cancellationType.shortName }}
            </p>

            <p v-if="_reservation.cancellationNote" class="ml-1">
              ({{ _reservation.cancellationNote }})
            </p>
          </div>
        </o-chip>
        <status-custom v-model="_reservation.statusType" :reservation="_reservation" :editable="reservation.allow.edit" />
      </o-description-details>

      <o-description-details
        :term="$t('general.notes')"
        term-class="mt-2 text-sm leading-5 text-gray-500 dark:text-gray-400"
      >
        <o-field-group v-if="reservation.allow.edit" v-slot="{ status }" :name="$t('general.notes')" rules="max:1000">
          <text-area-field
            v-model="_reservation.notes"
            size="sm"
            :placeholder="$t('reservation.addDetail')"
            base-class="-ml-2 w-full rounded-md border dark:bg-gray-900 border-transparent hover:border-gray-200 dark:hover:border-gray-800 focus:border-gray-200 dark:focus:border-gray-800 resize-none hover:resize focus:resize transition-colors duration-200"
            max-length="1000"
            :status="status"
          />
        </o-field-group>

        <p v-else class="pt-1">
          {{ _reservation.notes }}
        </p>
      </o-description-details>
    </dl>
  </div>
</template>

<script setup lang="ts">
import CustomerSelect from '@/components/customer/elements/Select.vue'
import Status from '@/components/reservation/elements/Status.vue'
import Price from '@/components/reservation/edit/Price.vue'
import Order from '@/components/reservation/edit/Order.vue'

import StatusCustom from '@/components/reservation/elements/StatusCustom.vue'
import { CourseRunStatus } from '@/_api/models/course'

import * as Fields from '@/components/fields'

import {
  ODescriptionDetails,
} from '@/components/elements'

const { $api, $date, $dateFns } = useNuxtApp()

// Register components that need explicit names
const SelectField = Fields.SelectField
const TextField = Fields.TextField
const TextAreaField = Fields.TextAreaField
const NumberField = Fields.NumberField

interface Props {
  reservation?: Record<string, any> | null
}

interface Emits {
  (e: 'update:reservation', value: any): void
  (e: 'update:loading', value: boolean): void
  (e: 'update'): void
  (e: 'recurring'): void
}

const props = withDefaults(defineProps<Props>(), {
  reservation: null,
})

const emit = defineEmits<Emits>()

const edit = ref({
  price: false,
})
const test = ref(new Date())

const _reservation = computed({
  get () {
    return props.reservation
  },
  set (value) {
    emit('update:reservation', value)
  },
})

const attendeeCount = computed(() => {
  if (props.reservation!.isGroup) {
    return props.reservation!.groupCustomers
      ?.filter((attendee: any) => !attendee.isStatusCancelled)
      ?.length
  }

  return 0
})

const isGroup = computed(() => props.reservation!.isGroup)
const isRecurring = computed(() => props.reservation!.isRecurring)
const isCourse = computed(() => props.reservation!.courseRunReservations?.length > 0)
const isStatusCancelled = computed(() => props.reservation!.isStatusCancelled)

const integrationsWithLabels = computed(() => {
  if (props.reservation!.integrations) {
    return props.reservation!.integrations.filter((integration: any) => integration.labels.length > 0)
  }

  return null
})

const date = computed({
  get () {
    const d = $date.parse(props.reservation!.date.value)
    const time = props.reservation!.startTime.value

    return $date.add(d, { minutes: time })
  },
  set (val) {
    const d = $dateFns.startOfDay(val)
    const time = $dateFns.differenceInMinutes(val, d)

    _reservation.value!.date.value = $date.format(d)
    _reservation.value!.startTime.value = time
  },
})

function update (reservation: any) {
  emit('update:reservation', reservation)
}

function handleRecurring () {
  emit('recurring')
}

async function moveReservation (reservation: any) {
  emit('update:loading', true)

  try {
    const response = await $api.reservation.getWithDate(
      String(props.reservation!.idReservation),
      props.reservation!.date.integer,
    )
    emit('update:reservation', response.data)
    emit('update:loading', false)
    emit('update')
  } catch (e) {
    emit('update:loading', false)
  }
}

function loading (loadingVal: boolean) {
  emit('update:loading', loadingVal)
}
</script>
